import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { brandingConfig } from '@/config/branding';
import { ContactMethodField } from './contact-method-field';
import { stepAboutYouSchema } from '../schemas/step-about-you';
import type { StepAboutYouData } from '../types';
import { useEmailValidation, type EmailStatus } from '../hooks/use-email-validation';

interface StepAboutYouProps {
  defaultValues: StepAboutYouData;
  onNext: (data: StepAboutYouData) => void;
  onBack: () => void;
  disabled?: boolean;
  emailReadOnly?: boolean;
}

const EmailStatusIndicator = ({ status }: { status: EmailStatus }) => {
  if (status === 'idle' || status === 'available') return null;

  const map: Record<string, { className: string; text: string }> = {
    checking: { className: 'text-muted-foreground', text: 'Checking...' },
    taken: { className: 'text-destructive', text: 'This email is already taken' },
    disposable: { className: 'text-destructive', text: 'Disposable emails are not allowed' },
    invalid: { className: 'text-destructive', text: 'Please enter a valid email' },
  };

  const entry = map[status];
  if (!entry) return null;

  return <p className={`text-sm ${entry.className}`}>{entry.text}</p>;
};

export function StepAboutYou({
  defaultValues,
  onNext,
  onBack,
  disabled = false,
  emailReadOnly = false,
}: StepAboutYouProps) {
  const { t } = useTranslation('auth');
  const { status: emailStatus, validate: validateEmail } = useEmailValidation();

  const form = useForm({
    resolver: zodResolver(stepAboutYouSchema),
    defaultValues,
    mode: 'onTouched',
  });

  const watchedEmail = form.watch('email');
  useEffect(() => {
    if (emailReadOnly) return;
    validateEmail(watchedEmail ?? '');
  }, [watchedEmail, validateEmail, emailReadOnly]);

  const isEmailBlocked = !emailReadOnly && (emailStatus === 'taken' || emailStatus === 'disposable');

  const handleSubmit = form.handleSubmit((values) => {
    if (isEmailBlocked) return;
    onNext(values as StepAboutYouData);
  });

  return (
    <Form {...form}>
      <form onSubmit={handleSubmit} className="space-y-5">
        <div className="space-y-1 text-center">
          <h2 className="text-foreground text-2xl font-bold tracking-tight">{t('signUpNew.step1.title')}</h2>
          <p className="text-muted-foreground text-sm">{t('signUpNew.step1.subtitle')}</p>
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4">
          <FormField
            control={form.control}
            name="firstName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('signUpNew.step1.firstName')}</FormLabel>
                <FormControl>
                  <Input
                    placeholder={t('signUpNew.step1.firstNamePlaceholder')}
                    autoComplete="given-name"
                    disabled={disabled}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="lastName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('signUpNew.step1.lastName')}</FormLabel>
                <FormControl>
                  <Input
                    placeholder={t('signUpNew.step1.lastNamePlaceholder')}
                    autoComplete="family-name"
                    disabled={disabled}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('signUpNew.step1.email')}</FormLabel>
              <FormControl>
                <Input
                  type="email"
                  placeholder={t('signUpNew.step1.emailPlaceholder')}
                  autoComplete="email"
                  disabled={disabled || emailReadOnly}
                  readOnly={emailReadOnly}
                  {...field}
                />
              </FormControl>
              {!emailReadOnly && <EmailStatusIndicator status={emailStatus} />}
              <FormMessage />
            </FormItem>
          )}
        />

        <ContactMethodField
          selectedMethods={form.watch('contactMethods') ?? []}
          onMethodsChange={(v) => form.setValue('contactMethods', v, { shouldValidate: true })}
          whatsappCountryCode={form.watch('whatsappCountryCode')}
          whatsappNumber={form.watch('whatsappNumber')}
          telegramUsername={form.watch('telegramUsername')}
          onWhatsappCountryCodeChange={(v) => form.setValue('whatsappCountryCode', v)}
          onWhatsappNumberChange={(v) => form.setValue('whatsappNumber', v, { shouldValidate: true })}
          onTelegramUsernameChange={(v) => form.setValue('telegramUsername', v, { shouldValidate: true })}
          disabled={disabled}
          errors={{
            contactMethods: form.formState.errors.contactMethods?.message,
            whatsappNumber: form.formState.errors.whatsappNumber?.message,
            telegramUsername: form.formState.errors.telegramUsername?.message,
          }}
        />

        <FormField
          control={form.control}
          name="termsAccepted"
          render={({ field }) => (
            <FormItem className="flex items-start gap-2">
              <FormControl>
                <Checkbox
                  checked={field.value}
                  onCheckedChange={field.onChange}
                  disabled={disabled}
                  className="mt-0.5"
                />
              </FormControl>
              <div className="grid gap-1 leading-none">
                <label className="text-muted-foreground cursor-pointer text-sm leading-relaxed">
                  {t('signUp.termsPrefix')}{' '}
                  {brandingConfig.appUrl ? (
                    <a
                      href={`${brandingConfig.appUrl}/terms`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-foreground hover:text-primary underline underline-offset-4"
                    >
                      {t('signUp.termsLink')}
                    </a>
                  ) : (
                    <span className="text-foreground">{t('signUp.termsLink')}</span>
                  )}{' '}
                  {t('signUp.termsAnd')}{' '}
                  {brandingConfig.appUrl ? (
                    <a
                      href={`${brandingConfig.appUrl}/privacy`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-foreground hover:text-primary underline underline-offset-4"
                    >
                      {t('signUp.privacyLink')}
                    </a>
                  ) : (
                    <span className="text-foreground">{t('signUp.privacyLink')}</span>
                  )}
                </label>
                <FormMessage />
              </div>
            </FormItem>
          )}
        />

        <Button
          type="submit"
          className="w-full"
          disabled={disabled || !form.formState.isValid || isEmailBlocked}
        >
          {t('signUpNew.continue')}
        </Button>

        <Button type="button" variant="ghost" className="w-full" onClick={onBack} disabled={disabled}>
          {t('signUpNew.back')}
        </Button>
      </form>
    </Form>
  );
}
